import React from "react";

import Layout from "../components/Layout";

const lc = "font-semibold underline !text-[#1858fd]";

const Page = () => {
  return (
    <Layout>
      <section className="pt-12 md:pt-16 pb-22 md:pb-32">
        <div className="container">
          <h1>Cookie Policy</h1>
          <p>
            <em>Last Updated: June 3, 2026</em>
          </p>
          <p>
            Nouvel Technologies Inc. and its related entities (collectively,
            "Nouvel Technologies Inc.", "our", "we" or "us") take your privacy
            seriously and encourage you to understand our use of automated data
            collection mechanisms in connection with our website{" "}
            <a
              href="https://nouveltechnologies.com/"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://nouveltechnologies.com/
            </a>{" "}
            and its related domains, platforms, social media channels, software
            applications, mobile applications, services, information, web
            postings, products and tools (collectively, "Service(s)" or
            "Website"). As discussed in greater detail within this Cookie
            Policy, our collection of data through the use of these technologies
            helps us to provide you with a positive experience and potential
            offerings when you browse our Services.
          </p>
          <p>
            <strong>
              Nouvel Technologies Inc. uses cookies and pixels as automatic data
              collection mechanisms that collect information, including in some
              instances personal information, from users of its Services. Third
              parties also place these tools on our Website to collect and
              process information about our users for various purposes, including
              for security, for analytics, and to facilitate targeted
              advertising. These tools include without limitation:{" "}
              <a
                href="https://developers.google.com/recaptcha/"
                target="_blank"
                rel="noreferrer"
                className={lc}
              >
                Google reCAPTCHA
              </a>
              ,{" "}
              <a
                href="https://policies.google.com"
                target="_blank"
                rel="noreferrer"
                className={lc}
              >
                Google Analytics
              </a>
              ,{" "}
              <a
                href="https://www.zoominfo.com/about-zoominfo/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className={lc}
              >
                ZoomInfo
              </a>
              , and{" "}
              <a
                href="https://www.dealfront.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
                className={lc}
              >
                Leadfeeder
              </a>
              . Please carefully review the linked information associated with
              each tool, which is also set forth below.
            </strong>
          </p>
          <p>
            <strong>
              By visiting our Website, or otherwise using our Services, you
              consent to Nouvel Technologies Inc. use of cookies and pixels as
              detailed in this Cookie Policy.
            </strong>{" "}
            Please visit our Cookie Chart below for further details about which
            cookies and pixels we use in connection with our Services. Except
            for essential cookies, all cookies and pixels will expire after the
            associated period identified in the Cookie Chart. If you have any
            questions or concerns about our use of cookies, please send us an
            email at{" "}
            <a
              href="https://www.nouveltechnologies.com"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              www.nouveltechnologies.com
            </a>
          </p>

          <h3>I. What kinds of technologies are used?</h3>

          <h4>
            <u>Cookies.</u>
          </h4>
          <p>
            A cookie is a small text file that we store on your browser or the
            hard drive of your computer. They are used to make websites function
            and to collect information to facilitate more secure, efficient, and
            customized user experiences. Cookie use is standard for most
            websites.
          </p>
          <p>
            Nouvel Technologies Inc. uses four types of cookies in connection
            with our Services:
          </p>
          <ul className="list-disc ml-6">
            <li>
              <p>
                <strong>Required cookies.</strong> These are cookies that are
                required for the operation of our Services. These essential
                cookies are always enabled because our Services won't work
                properly without them. We use some cookies to help us identify
                and control trusted web traffic, as well as detect and prevent
                illegal or malicious activity. You cannot opt out of these
                cookies because they are strictly necessary to run our Website.
              </p>
            </li>
            <li>
              <p>
                <strong>Functional cookies.</strong> These cookies enable basic
                features of the Website to function properly, such as page
                navigation, video playback, or image displays.
              </p>
            </li>
            <li>
              <p>
                <strong>Performance cookies.</strong> These cookies help us
                analyze how users interact with the Website and improve user
                experience, functionality, and performance. This includes
                recognizing and counting the number of visitors and seeing how
                visitors move around our Website when they are using it.
              </p>
            </li>
            <li>
              <p>
                <strong>Targeting cookies.</strong> Our Website may contain
                links and plug-ins to social media sites (e.g., Facebook,
                Instagram, Twitter, YouTube, LinkedIn). If you click on these
                plug-ins or links, cookies enable these social media sites to
                collect information and they may use it to deliver
                advertisements that are relevant and meaningful to you. We have
                no control over these social media sites and we are not
                responsible for their practices.
              </p>
            </li>
          </ul>

          <h4>
            <u>Pixels.</u>
          </h4>
          <p>
            Pixels - sometimes also called "pixel tags," "clear gifs," or "web
            beacons" - are very small graphic images that help Nouvel
            Technologies Inc. to analyze users' behavior in connection with our
            Services and to measure the effectiveness of our Services and
            related advertising. Nouvel Technologies Inc. uses pixel tags on our
            Website to collect information about users' use of our Services,
            such as which Website pages you view, which features or links you
            use, or other such actions you take relative to our Services. Types
            of information that pixels may gather includes the IP address of the
            device you use to access the Services, date and time information,
            immediate browsing history, and technical information about your
            device such as the browser type and operating system. Pixels can
            also allow us, or third parties, to place cookies on your device.
          </p>

          <h3>II. What is the purpose of these technologies?</h3>
          <p>
            We use cookies and pixels in connection with our Services for the
            purposes of furthering security, customization, analytics, and
            advertising. We, or our service providers on our behalf, use the
            technologies described above for the following purposes.
          </p>

          <h4>
            <u>Security.</u>
          </h4>
          <p>
            Cookies support our Services' security features, helping to make
            your experience with our Services more secure. For instance, we can
            use cookie data to help identify malicious or illegal behavior or
            other activity which violates our Terms of Use.
          </p>

          <h4>
            <u>Customization.</u>
          </h4>
          <p>
            Cookies allow us to retain your preferred settings for our Services
            and deliver a customized experience to you, such as by saving your
            username and other profile information, previous searches, and
            information regarding your previously viewed products or services.
            By retaining this information, we can present you with information
            that is consistent with your prior use of our Services and likely
            interests.
          </p>

          <h4>
            <u>Analytics.</u>
          </h4>
          <p>
            We want to provide you with features that interest you. Cookies
            allow us to determine which aspects of our Services are more (or
            less) popular with our users, including our websites, e-mails,
            advertisements, or other content. We can use that information to
            understand and improve upon our Services, as well as to develop new
            features to serve you better. Such usage information forms the basis
            for reports generated to provide us aggregated information about the
            utilization of our Services.
          </p>

          <h4>
            <u>Advertising and Social Media.</u>
          </h4>
          <p>
            Marketing cookies and pixels permit more efficient delivery of
            advertisements to you, regarding products or services in which you
            are more likely to be interested. They also help us track the
            effectiveness of our marketing efforts. Social media cookies and
            pixels can track your browser activity across other sites and create
            a profile of things that are likely of interest to you.
          </p>

          <h3>
            III. Do third parties use these technologies relating to Nouvel
            Technologies Inc.'s products and services?
          </h3>
          <p>
            Yes. Please note that, as identified in our Cookie Chart, third
            parties also use cookies in connection with our Services. These
            named third parties may include, for example, advertising networks
            and providers of external services like web traffic analysis
            services. Select third-party technologies, as reflected in the
            comprehensive Cookie Chart, include, but are not limited to, the
            following:
          </p>

          <h4>Google reCAPTCHA v3.</h4>
          <p>
            We use "Google reCAPTCHA v3" (hereinafter "reCAPTCHA"), provided by
            Google, Inc. ("Google"), on our Website. The purpose of reCAPTCHA
            is to keep our website as secure as possible by checking whether
            data that enters our system - for example, through a web form - is
            provided by a human individual as opposed to spamming software or
            other robot. To make this determination for this purpose, reCAPTCHA
            collects and reviews various aspects of the Website user's activity.
            This assessment automatically begins when our user enters the
            Website. For the purpose of confirming human activity, reCAPTCHA
            assesses various information that it collects - including, without
            limitation, IP address, browser information cursor activity, and
            time spent on the Website. reCAPTCHA will also place an additional
            cookie in your browser upon its assessment of your activity and take
            a snapshot of your browser window. Data collected during the
            analysis is forwarded to Google.
          </p>
          <p>
            Please note that the reCAPTCHA collection, assessment, and other
            processing runs completely in the background. While reCAPTCHA
            processing is noted for our users both here, Website visitors are
            not informed that an analysis is taking place when it occurs. As
            opposed to former CAPTCHA versions, which required website visitors
            to identify images, solve puzzles, or check a box, reCAPTCHA
            assesses and scores user input for human origin without any such
            affirmative behavior from the user.
          </p>
          <p>
            Our goal in using the reCAPTCHA tool on the Website is to protect
            the Services, and users of the Services, from bots, spam, and other
            such unapproved and potentially malicious activity. If you want to
            prevent any data about you and your behavior to be transmitted to
            Google, you must log out of Google and delete all Google cookies
            before visiting our Website or using the reCAPTCHA software. Please
            visit the following page to obtain more information about reCAPTCHA:{" "}
            <a
              href="https://developers.google.com/recaptcha/"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://developers.google.com/recaptcha/
            </a>
            . Additional information on Google reCAPTCHA can be found here,{" "}
            <a
              href="https://www.google.com/recaptcha/intro/android.html"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://www.google.com/recaptcha/intro/android.html
            </a>
            , and Google's privacy policy can be found here:{" "}
            <a
              href="https://www.google.com/intl/de/policies/privacy/"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://www.google.com/intl/de/policies/privacy/
            </a>
            .
          </p>

          <h4>Google Analytics.</h4>
          <p>
            Our Website uses Google Analytics, provided by Google. This web
            analytics program collects and generates information, through cookie
            placement, about your use of the Website. Google will use this
            information to evaluate your use of our Website, to compile reports
            on Website activity and to provide us with other services related to
            Website activity and internet usage. The IP address provided to us
            by Google Analytics will not be merged with other data that Google
            provides to us.
          </p>
          <p>
            If you would like to prevent Google Analytics from collecting your
            data, you can prevent this collection by utilizing the opt-out tool
            that Google makes available at the following site:{" "}
            <a
              href="http://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              http://tools.google.com/dlpage/gaoptout
            </a>
            . If you use this tool, an opt-out cookie will be placed to prevent
            Google Analytics from collecting your information. Google's Terms of
            Use and privacy policy can be found on this page:{" "}
            <a
              href="https://policies.google.com"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://policies.google.com
            </a>
            .
          </p>

          <h4>ZoomInfo.</h4>
          <p>
            Our Website uses cookies set by ZoomInfo, Inc. ("ZoomInfo"), a
            business-to-business data solutions provider. ZoomInfo uses
            Cloudflare cookies, including _cfuvid, to manage web traffic and
            enhance security on its platform. These cookies help distinguish
            between human visitors and automated bots, and support rate limiting
            to ensure reliable service. The _cfuvid cookie is a session cookie
            that expires when you close your browser. For more information about
            ZoomInfo's privacy practices, please visit:{" "}
            <a
              href="https://www.zoominfo.com/about-zoominfo/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://www.zoominfo.com/about-zoominfo/privacy-policy
            </a>
            .
          </p>

          <h4>Leadfeeder.</h4>
          <p>
            Our Website uses Leadfeeder (now part of Dealfront), a web
            analytics tool that identifies companies visiting our Website.
            Leadfeeder places the _lfa cookie to track browsing activity and
            attribute website visits to specific organizations. This information
            helps us understand which businesses are interested in our services.
            The _lfa cookie expires after approximately one year. For more
            information about Leadfeeder's privacy practices, please visit:{" "}
            <a
              href="https://www.dealfront.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
              className={lc}
            >
              https://www.dealfront.com/privacy-policy/
            </a>
            .
          </p>

          <h3>IV. Cookie Chart</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-3 py-2 text-left">
                    Cookie Name
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left">
                    Cookie Category
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left">
                    Purpose for Cookie
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left">
                    Expiration
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left">
                    First or Third Party
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    _GRECAPTCHA
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Required
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google reCAPTCHA sets this cookie to verify that website
                    visitors are human and to protect against spam and abuse.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    6 months
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    __SecureBUCKET
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Required
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google sets this cookie to ensure the security and integrity
                    of web requests.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    6 months
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">AEC</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Required
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google sets this cookie to ensure that requests within
                    browsing sessions are made by the user, and not by other
                    sites or bots.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    6 months
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    __SecureSTRIP
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Required
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google sets this cookie for security purposes, to manage
                    session integrity and protect against cross-site request
                    forgery.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">Session</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">_cfuvid</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Required
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    ZoomInfo (Cloudflare) sets this cookie for bot detection and
                    rate limiting to distinguish between human users and
                    automated traffic.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">Session</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">_ga</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Performance
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google Analytics sets this cookie to calculate visitor,
                    session, and campaign data and to track site usage for
                    analytics reports.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    1 year, 1 month, 4 days
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">
                    _ga_JTNVWSHC3F
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Performance
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google Analytics sets this cookie to store and count page
                    views.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    1 year, 1 month, 4 days
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">_lfa</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Performance
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Leadfeeder (Dealfront) sets this cookie to track browsing
                    activity and identify companies visiting the Website.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">1 year</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2">NID</td>
                  <td className="border border-gray-300 px-3 py-2">
                    Targeting
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Google sets this cookie to store user preferences and to
                    personalize advertisements on Google services.
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    6 months
                  </td>
                  <td className="border border-gray-300 px-3 py-2">
                    Third Party
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Page;
